import { getTransactions } from "./apiClient";
import { USE_LOCAL_FALLBACK } from "../config/firebaseConfig";
import { db } from "../services/firebase";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, setDoc, getDoc, query, where } from "firebase/firestore";

function normalizePayment(p) {
  const studentId = String(p.studentId || p.student_id || "").trim();
  const campus = String(p.campus || p.Campus || "").toLowerCase();
  const method = p.method || p.Method || "";
  const reference = p.reference || p.Reference || "";
  const amount = Number(p.amount || p.Amount || 0);
  const date = p.date || p.Date || "";
  const accountId = p.accountId || p.account_id || null;
  const category = p.category || p.Category || "";
  return {
    ...p,
    studentId,
    campus,
    method,
    reference,
    amount,
    date,
    accountId,
    category,
  };
}

export async function getPayments() {
  if (!USE_LOCAL_FALLBACK) {
    try {
      const snap = await getDocs(collection(db, "payments"));
      const rows = snap.docs.map(d => normalizePayment({ id: d.id, ...d.data() }));
      // refresh cache for offline views
      localStorage.setItem("payments_fs_cache", JSON.stringify(rows));
      return rows;
    } catch {
      // fall back to cached Firestore or local legacy
      try {
        const raw = localStorage.getItem("payments_fs_cache");
        const cached = raw ? JSON.parse(raw) : [];
        return Array.isArray(cached) ? cached.map(normalizePayment) : [];
      } catch {
        return [];
      }
    }
  } else {
    // Development/local fallback: use local storage first, then optional API
    let local = [];
    try {
      const raw = localStorage.getItem("payments");
      local = raw ? JSON.parse(raw) : [];
    } catch {
      local = [];
    }
    if (local && local.length) return local;
    try {
      const txs = await getTransactions();
      const mapped = txs.map(t => normalizePayment({
        date: t.date,
        student_id: t.student_id || "",
        campus: t.campus || "",
        method: t.method || "",
        reference: t.reference || "",
        amount: Number(t.amount || 0),
        account_id: t.account_id || null,
      }));
      return mapped;
    } catch {
      return [];
    }
  }
}

export function addPayment(newPayment) {
  if (!USE_LOCAL_FALLBACK) {
    // Idempotent write: if reference present, use deterministic document id: {campus}|{reference}
    const ref = String(newPayment.reference || "").trim();
    const campus = String(newPayment.campus || "").toLowerCase();
    if (ref) {
      const docId = `${campus}|${ref}`;
      return setDoc(doc(db, "payments", docId), newPayment, { merge: false }).then(async () => {
        const snap = await getDocs(collection(db, "payments"));
        const rows = snap.docs.map(d => normalizePayment({ id: d.id, ...d.data() }));
        localStorage.setItem("payments_fs_cache", JSON.stringify(rows));
        return rows;
      });
    }
    return addDoc(collection(db, "payments"), newPayment).then(async () => {
      const snap = await getDocs(collection(db, "payments"));
      const rows = snap.docs.map(d => normalizePayment({ id: d.id, ...d.data() }));
      localStorage.setItem("payments_fs_cache", JSON.stringify(rows));
      return rows;
    });
  } else {
    let local = [];
    try {
      const raw = localStorage.getItem("payments");
      local = raw ? JSON.parse(raw) : [];
    } catch {
      local = [];
    }
    // Idempotent local write by (campus|reference) key when reference present
    const ref = String(newPayment.reference || "").trim();
    if (ref) {
      const key = `${String(newPayment.campus || "").toLowerCase()}|${ref}`;
      const map = new Map(local.map(p => [`${String(p.campus || "").toLowerCase()}|${String(p.reference || "").trim()}`, p]));
      map.set(key, normalizePayment(newPayment));
      const updated = Array.from(map.values());
      localStorage.setItem("payments", JSON.stringify(updated));
      return updated;
    }
    const updated = [...local, normalizePayment(newPayment)];
    localStorage.setItem("payments", JSON.stringify(updated));
    return updated;
  }
}

export function deletePayment(id) {
  if (!USE_LOCAL_FALLBACK) {
    return (async () => {
      // Fetch the document to discover campus/reference and remove all duplicates (random-id + Campus|OR)
      let campus = "";
      let reference = "";
      try {
        const docRef = doc(db, "payments", String(id));
        const snapDoc = await getDoc(docRef);
        if (snapDoc.exists()) {
          const data = snapDoc.data() || {};
          campus = String(data.campus || "").toLowerCase();
          reference = String(data.reference || "").trim();
        }
      } catch (e) {
        // ignore read error
      }
      // Delete the requested id
      try { await deleteDoc(doc(db, "payments", String(id))); } catch (e) {
        // ignore if already gone
      }
      // Delete the canonical Campus|OR if different
      if (campus && reference) {
        const canonId = `${campus}|${reference}`;
        if (canonId !== String(id)) {
          try { await deleteDoc(doc(db, "payments", canonId)); } catch (e) {
            // ignore
          }
        }
        // Also search for any stray duplicates with same campus+reference and delete them
        try {
          // Prefer single-field query to avoid composite index requirement
          const q1 = query(collection(db, "payments"), where("campus", "==", campus));
          const snap1 = await getDocs(q1);
          for (const d of snap1.docs) {
            const data = d.data() || {};
            if (String(data.reference || "").trim() === reference) {
              if (String(d.id) !== String(id) && String(d.id) !== `${campus}|${reference}`) {
                try { await deleteDoc(doc(db, "payments", d.id)); } catch (e) { /* ignore */ }
              }
            }
          }
        } catch (e) {
          // Ultimate fallback: scan all and filter client-side (low volume expected)
          try {
            const snapAll = await getDocs(collection(db, "payments"));
            for (const d of snapAll.docs) {
              const data = d.data() || {};
              const c = String(data.campus || "").toLowerCase();
              const r = String(data.reference || "").trim();
              if (c === campus && r === reference) {
                if (String(d.id) !== String(id) && String(d.id) !== `${campus}|${reference}`) {
                  try { await deleteDoc(doc(db, "payments", d.id)); } catch (err) { /* ignore */ }
                }
              }
            }
          } catch (err) { /* ignore */ }
        }
      }
      // Refresh cache
      const snapAll = await getDocs(collection(db, "payments"));
      const rows = snapAll.docs.map(d => normalizePayment({ id: d.id, ...d.data() }));
      localStorage.setItem("payments_fs_cache", JSON.stringify(rows));
      return rows;
    })();
  } else {
    try {
      const raw = localStorage.getItem("payments");
      let arr = raw ? JSON.parse(raw) : [];
      // Remove by id
      arr = arr.filter(p => String(p.id) !== String(id));
      // If entry had campus|reference variants, remove them too
      const keySet = new Set(arr.map(p => `${String(p.campus || "").toLowerCase()}|${String(p.reference || "").trim()}`));
      const toRemove = [];
      arr.forEach(p => {
        const k = `${String(p.campus || "").toLowerCase()}|${String(p.reference || "").trim()}`;
        if (k && keySet.has(k)) {
          // keep one copy only; mark duplicates
          if (!toRemove.includes(k)) toRemove.push(k);
        }
      });
      const updated = arr.filter((p, idx, a) => {
        const k = `${String(p.campus || "").toLowerCase()}|${String(p.reference || "").trim()}`;
        if (!k) return true;
        // keep the first occurrence, drop later ones
        const firstIdx = a.findIndex(x => `${String(x.campus || "").toLowerCase()}|${String(x.reference || "").trim()}` === k);
        return idx === firstIdx;
      });
      localStorage.setItem("payments", JSON.stringify(updated));
      return updated;
    } catch (e) {
      return [];
    }
  }
}

export function updatePayment(id, patch) {
  if (!USE_LOCAL_FALLBACK) {
    return updateDoc(doc(db, "payments", String(id)), patch).then(async () => {
      const snap = await getDocs(collection(db, "payments"));
      const rows = snap.docs.map(d => normalizePayment({ id: d.id, ...d.data() }));
      localStorage.setItem("payments_fs_cache", JSON.stringify(rows));
      return rows;
    });
  } else {
    try {
      const raw = localStorage.getItem("payments");
      const arr = raw ? JSON.parse(raw) : [];
      const updated = arr.map(p => (String(p.id) === String(id) ? { ...p, ...patch } : p));
      localStorage.setItem("payments", JSON.stringify(updated));
      return updated.map(normalizePayment);
    } catch {
      return [];
    }
  }
}
